// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { createDataProvider } from '@shared/ui-kit/providers/data-provider/create-data-provider';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useMemo, useEffect } from 'react';
import type {
  SourceAsset,
  SellOperationData,
  RefundType,
} from '@shared/portfolio-operations/types';
import {
  sellOperationFormSchema,
  type SellOperationFormData,
} from '@shared/portfolio-operations/schemas/sell-operation';
import {
  calculateSharesFromPercentage,
  calculateAmountFromPercentage,
  calculatePercentageFromShares,
  calculatePercentageFromAmount,
} from '@shared/portfolio-operations/utils/transfer-calculations';

export interface SellOperationProviderProps {
  sourceAsset: SourceAsset;
  onFormDataChange?: (data: SellOperationData) => void;
  onFormValidationChange?: (isValid: boolean) => void;
}

interface SellOperationContextValue {
  sourceAsset: SourceAsset;
  form: ReturnType<typeof useForm<SellOperationFormData>>;
  totalShares: number;
  totalAmount: number;
  currency: string;
  onSaleTypeChange: (type: RefundType) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  formData: SellOperationData;
}

const useSellOperationFactory = ({
  sourceAsset,
  onFormDataChange,
  onFormValidationChange,
}: SellOperationProviderProps): SellOperationContextValue => {
  const totalShares = sourceAsset.quantity;
  const totalAmount = sourceAsset.investment;
  const currency = sourceAsset.currency;

  const form = useForm<SellOperationFormData>({
    resolver: zodResolver(sellOperationFormSchema),
    mode: 'onChange',
    defaultValues: {
      type: 'full',
      percentage: 100,
      shares: totalShares,
      amount: totalAmount,
      inputType: 'amount',
    },
  });

  const { watch, setValue, getValues, formState } = form;

  const saleType = watch('type');
  const salePercentage = watch('percentage');
  const saleShares = watch('shares');
  const saleAmount = watch('amount');
  const saleInputType = watch('inputType');

  const formData: SellOperationData = useMemo(
    () => ({
      type: saleType,
      percentage: salePercentage,
      shares: saleShares,
      amount: saleAmount,
      inputType: saleInputType,
    }),
    [saleType, salePercentage, saleShares, saleAmount, saleInputType],
  );

  const onSaleTypeChange = useCallback(
    (type: RefundType) => {
      const opts = { shouldValidate: true as const };
      switch (type) {
        case 'full':
          setValue('percentage', 100, opts);
          setValue('shares', totalShares, opts);
          setValue('amount', totalAmount, opts);
          break;
        case 'partial': {
          const defaultPct = 50;
          setValue('percentage', defaultPct, opts);
          setValue(
            'shares',
            calculateSharesFromPercentage(defaultPct, totalShares),
            opts,
          );
          setValue(
            'amount',
            calculateAmountFromPercentage(defaultPct, totalAmount),
            opts,
          );
          break;
        }
        default:
          return;
      }
      setValue('type', type, opts);
    },
    [setValue, totalShares, totalAmount],
  );

  const onBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      if (saleType !== 'partial') return;

      const opts = { shouldValidate: true as const };
      const name = e.target.name as 'percentage' | 'shares' | 'amount';

      switch (name) {
        case 'percentage': {
          const percentage = getValues('percentage');
          const shares = calculateSharesFromPercentage(percentage, totalShares);
          const amount = calculateAmountFromPercentage(percentage, totalAmount);
          setValue('percentage', percentage, opts);
          setValue('shares', shares, opts);
          setValue('amount', amount, opts);
          break;
        }
        case 'shares': {
          const shares = getValues('shares');
          const percentage = calculatePercentageFromShares(shares, totalShares);
          const amount = calculateAmountFromPercentage(percentage, totalAmount);
          setValue('percentage', percentage, opts);
          setValue('shares', shares, opts);
          setValue('amount', amount, opts);
          setValue('inputType', 'shares', opts);
          break;
        }
        case 'amount': {
          const amount = getValues('amount');
          const percentage = calculatePercentageFromAmount(amount, totalAmount);
          const shares = calculateSharesFromPercentage(percentage, totalShares);
          setValue('percentage', percentage, opts);
          setValue('shares', shares, opts);
          setValue('amount', amount, opts);
          setValue('inputType', 'amount', opts);
          break;
        }
        default:
          return;
      }
    },
    [saleType, totalShares, totalAmount, setValue, getValues],
  );

  useEffect(() => {
    onFormDataChange?.(formData);
  }, [formData, onFormDataChange]);

  useEffect(() => {
    onFormValidationChange?.(formState.isValid);
  }, [formState.isValid, onFormValidationChange]);

  return {
    sourceAsset,
    form,
    totalShares,
    totalAmount,
    currency,
    onSaleTypeChange,
    onBlur,
    formData,
  };
};

export const { SellOperationProvider, useSellOperationData } = createDataProvider({
  name: 'SellOperation',
  factory: useSellOperationFactory,
});
